import React, { useEffect, useState , useMemo} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import dateFormat from "dateformat";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import Tooltip from "src/components/tooltip";
import InfiniteScroll from "src/components/infiniteScroll";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/utils/file.service";
import { AddLearnersDialog } from "./add";

import { Course } from "src/types/coaching/courses";
import { Podcast } from "src/types/coaching/podcast";
import { Simulation } from "src/types/coaching/simulation";
import { getDueText } from "src/pages/coaching/components/course/view";

import { flattenContents } from "../../helper";

import {
  fetchCourseLearners,
} from "src/redux/actions/coaching/learners";

import _ from "lodash";
import cx from "classnames";
import styles from "./learners.module.scss";

interface CourseLearnersProps {
  id: string;
  course: Course | Podcast | Simulation;
}

export const CourseLearners: React.FC<CourseLearnersProps> = ({ id, course }) => {
  const dispatch = useDispatch();

  const { loading, learners, totalRecordsCount, currentPage, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const learnersState = _.get(state, "courses.learners");
      const loading = _.get(learnersState, "loading");
      const pagination = _.get(learnersState, "pagination");
      const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const learners = _.get(learnersState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        learners: learners,
        totalRecordsCount: totalRecordsCount,
        currentPage: currentPage,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  const [showAddLearner, setShowAddLearner] = useState(false);
  const [selectedLearner, setSelectedLearner] = useState({
    value: "ALL",
    label: "All learners",
  });

  const learnerOptions = [
    { label: "All learners", value: "ALL"},
    { label: "Completed", value: "COMPLETED" },
    { label: "In progress", value: "IN_PROGRESS", show: course?.entityType !== "simulation" },
    { label: "Not started", value: "NOT_STARTED"},
    { label: "Overdue", value: "OVERDUE"},
  ].filter(option => option.show !== false);
  
  const handleSelectionChange = (option: LearnerOption) => {
    setSelectedLearner(option);

    if (id) {
      const request = {
        course: id,
        status: option.value,
        page: 0,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  };

  const handleLoadMoreLearners = () => {
    if (nextPage && id) {
      const request = {
        course: id,
        status: selectedLearner.value,
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  };

  const handleAddLearnersFinished = () => {
    setShowAddLearner(false);
    handleSelectionChange(learnerOptions[0]);
  }

  return (
    <div className={styles.learnersContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
        <span className={styles.title}>{selectedLearner.label} ({totalRecordsCount})
        </span>
        <div className={styles.dropDown}>
            <LearnerTypeDropDown
            selected={selectedLearner}
            onChange={(action) => handleSelectionChange(action)}
            options={learnerOptions}
            />
          </div>
        </div>
        <div
          className={styles.addLearner}
          onClick={() => setShowAddLearner(true)}
        >
          <i className="paperflite-add" />
          <span>Add learners</span>
        </div>
      </div>
      <div className={styles.stacks}>
        <InfiniteScroll
          className={styles.scroll}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreLearners}
          loader={() => {
            return _.times(_.size(learners) === 0 || currentPage === 0 ? 2 : 1, (index) => (
              <LearnerPlaceholder key={index} />
            ));
          }}
        >
          {_.size(learners) === 0 && !loading && (
            <span className={styles.noRecordsFound}>No learners.</span>
          )}

          <div className={styles.learnersList}>
            {learners.map((learner, index) => (
              <LearnerRow key={index} learner={learner} course={course} />
            ))}
          </div>
        </InfiniteScroll>
      </div>

      {showAddLearner && (
        <AddLearnersDialog
          show={showAddLearner}
          onFinish={handleAddLearnersFinished}
          onClose={() => setShowAddLearner(false)}
        />
      )}
    </div>
  );
};

interface LearnerRowProps {
  learner: any;
  course: any;
}

const LearnerRow: React.FC<LearnerRowProps> = ({ learner, course }) => {
  const navigate = useNavigate();

  const name = useMemo(() => {
    return `${learner.user.firstName || ""} ${learner.user.lastName || ""}`;
  }, [learner]);

  const entityType = useMemo(() => {
    return _.get(course, "entityType");
  }, [course]);

  const score = Math.round(learner?.score * 100);

  const passed = useMemo(() => {
    let pass = learner.status === "COMPLETED";

    if (entityType === "course") {
      pass = score >= course.passPercentage;
    }
    return pass;
  }, [learner]);
  
  const dueDate = useMemo(() => {
    const date = _.get(learner, "dueDate");
    return date ? new Date(date) : null;
  }, [learner]);

  const isOverdue = dueDate && moment(dueDate).isBefore(moment(), "day");

  const dueText = getDueText(dueDate, learner.status);

  const showDueTimer =
  (learner.status === "IN_PROGRESS" && learner.dueDate) ||
  ((learner.status === "COMPLETED" || learner.status === "NOT_STARTED") && isOverdue);

  const getProgressContent = (learner) => {
    const score = Math.round(learner?.score * 100);
    // In progress status
    if (learner.status === "IN_PROGRESS" && entityType === "podcast") {  
      return FileService.formatDuration(learner.duration, "short");
    }

    // Completed status
    if (learner.status === "COMPLETED") {
      switch (entityType) {
        case "simulation": {
            if (score === 0) {
              return "na";
            }
            return `${score}%`;
          }

        case "podcast":
          return FileService.formatDuration(learner.duration, "short");

        case "course":
          const allContents = flattenContents(course?.contents || []);
          const assessment = allContents?.some(
            (c) => c.entityType === "assessment"
          );
        
          if (!assessment) {
            return null; 
          }
          return (
            <span
              className={cx(styles.score, {
                [styles.pass]: passed,
                [styles.fail]: !passed,
              })}
            >
              {`${score}%`}
            </span>
          );
      }
    }
  };

  const handleNavigate = (learnerId: string) => {
    if (learner.status !== "COMPLETED") return;
  
    const { entityType, id } = course;
  
    const reportPaths: Record<string, string> = {
      simulation: `/coaching/manage/simulations/${id}/reports/${learnerId}`,
      course: `/coaching/manage/courses/${id}/reports/${learnerId}`,
    };
  
    const path = reportPaths[entityType];
    if (path) navigate(path);
  };

  return (
    <div className={styles.learner} onClick={() => handleNavigate(learner.id)}>
      <div className={styles.details}>
        <div className={styles.avatar}>
        <Thumbnail
          media={learner.user?.icon}
          defaultImage="/images/assets/default_asset.jpg"
          className={styles.icon}
        />
        {showDueTimer && (
        <Tooltip content={dueText} placement="top">
          <i className={cx("paperflite-hour-glass", styles.dueIcon)} />
        </Tooltip>
        )}
        </div>
        <div className={styles.info}>
          <span className={styles.name}>
            {name}
            {passed && learner.status === "COMPLETED" && <i className="paperflite-flag" />}
            {!passed && learner.status === "COMPLETED" && <i className="paperflite-close" />}
          </span>
          <LearnerStatus learner={learner} course={course} />
        </div>
      </div>
      <div>
        <div className={styles.progress}>{getProgressContent(learner)}</div>
      </div>
    </div>
  );
};

export const LearnerStatus = ({ learner, course }) => {
  const entityType = _.get(course, "entityType");
  const assignedBy = _.get(learner, "assignedBy");
  const attempts = _.get(learner, "attempts", 0);
  const status = _.get(learner, "status");
  const dueDate = _.get(learner, "dueDate");

  const getLabel = () => {
    if (status === "NOT_STARTED") {
      if (dueDate) {
        return "due on";
      }
      return assignedBy ? "assigned on" : "enrolled on";
    }

    switch (entityType) {
      case "simulation":
        return "last call logged on";
      case "course":  
        let label = "completed on";
        if (status === "IN_PROGRESS") {
          label = "started on";
        }

        if (attempts > 1) {
          label = `${attempts} attempts | ${label}`;
        }

        return label;

      case "podcast":
        return "last engaged on";

      default:
        return "";
    }
  };

  const getDate = () => {
    if (status === "NOT_STARTED" && dueDate) {
      return moment.utc(dueDate).format("DD MMM YYYY");
    }
    return dateFormat(learner.modifiedDate, "dd mmm yyyy");
  };


  return (
    <span className={styles.engagedDate}>
      {getLabel()} {" "}
      {getDate()}
    </span>
  );
};


export const LearnerPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.learnerPlaceholder)}>
      <div className={styles.name}></div>
      <div className={styles.block}></div>
    </div>
  );
};


type LearnerOption = {
  value: string;
  label: string;
};

interface LearnerTypeDropDownProps {
  selected: LearnerOption;
  options: LearnerOption[];
  onChange: (option: LearnerOption) => void;
}

export const LearnerTypeDropDown: React.FC<LearnerTypeDropDownProps> = ({
  selected,
  options,
  onChange, 
}) => {
  const selectedOption = useMemo(
    () => options.find(option => option.value === selected.value),
    [selected, options]
  );

  const handleSelect = (value: string) => {
    const option = options.find(option => option.value === value);
    if (option) {
      onChange(option);
    }
  };

  return (
    <div className={styles.selectDropdown}>
      <Dropdown>
        <Dropdown.Toggle className={styles.button} as={"button"}>
        <span className={styles.filterList}></span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={styles.dropdown}
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: { offset: [0, 6] },
              },
            ],
          }}
        >
          {options.map((option, index) => (
            <Dropdown.Item
              key={index}
              className={cx(styles.dropdownItem, {
                [styles.active]: option.value === selectedOption?.value,
              })}
              onClick={() => handleSelect(option.value)}
            > 
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
